import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree,
} from '@angular/router';

import { AppService } from '../app.service';

/**
 * Requires a signed-in session, and nothing else.
 *
 * Deliberately not AuthorizeGuard: that one asks JWTTokenService for the
 * token, which reads a `session_token` cookie that nothing in this app sets -
 * login puts the JWT in localStorage as `xdbc.accessToken`. So it always
 * decides you are signed out, clears the token and bounces you to /login. No
 * route used it, so nothing surfaced that until one did.
 *
 * Deliberately not PlanGuard either, which is what the neighbouring routes
 * use: that gates on the workspace's allowance being spent. An invited member
 * is reading someone else's workspace, so their own allowance is not what
 * decides whether they may look - and accepting an invitation only to find the
 * workspace unopenable would be a strange dead end.
 *
 * Redirects rather than clearing anything. A guard that cannot find a session
 * has learned that it cannot find a session; that is not a reason to destroy
 * one that might still be there.
 */
@Injectable({ providedIn: 'root' })
export class SessionGuard implements CanActivate {

  constructor(private app: AppService, private router: Router) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree {
    if (this.app.get('xdbc.accessToken')) {
      return true;
    }
    return this.router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }
}
